import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { apiGet } from "../services/api";
import ClinicPrintHeader from "../components/print/ClinicPrintHeader";
import ClinicPrintFooter from "../components/print/ClinicPrintFooter";
import ReadonlyToothChart from "../components/manager/billing/ReadonlyToothChart";
import "./print.css";

interface RecordRow {
  tooth_number?: string;
  diagnosis?: string;
  procedure?: string;
  notes?: string;
  [key: string]: unknown;
}

interface PrescriptionRow {
  drug_name?: string;
  strength?: string;
  times_per_day?: string;
  days?: string;
  instructions?: string;
  [key: string]: unknown;
}

interface ChartSummaryShape {
  chart?: Record<string, { status?: string; crown?: string; surfaces?: Record<string, unknown> }>;
  [key: string]: unknown;
}

interface ClinicalBillItem {
  procedure_name?: string;
  teeth?: string;
  quantity?: number;
  unit_price?: number;
  total_price?: number;
}

interface ClinicalBillData {
  bill?: {
    id?: string | number;
    gross?: number;
    discount?: number;
    net?: number;
    status?: string;
    balance?: number;
    created_at?: string;
  };
  patient?: { patient_id?: string; name?: string };
  items?: ClinicalBillItem[];
  payments?: { id?: number; amount?: number; method?: string; date?: string }[];
  error?: string;
  [key: string]: unknown;
}

interface PharmacyBillItem {
  name?: string;
  qty?: number;
  unit_price?: number;
  amount?: number;
  type?: string;
}

interface PharmacyBillData {
  bill?: {
    id?: string | number;
    bill_type?: string;
    total?: number;
    created_at?: string;
  };
  patient?: { patient_id?: string; name?: string; mobile?: string };
  items?: PharmacyBillItem[];
  error?: string;
}

interface ReferenceInvoiceItem {
  id?: string | number;
  description?: string;
  quantity?: number;
  unit_price?: number;
  total_price?: number;
  [key: string]: unknown;
}

interface ReferenceInvoiceData {
  id?: string | number;
  invoice_no?: string;
  date?: string;
  patient_name?: string;
  doctor_name?: string;
  total_amount?: number;
  discount?: number;
  final_amount?: number;
  status?: string;
  notes?: string;
  items?: ReferenceInvoiceItem[];
  [key: string]: unknown;
}

interface ReferenceInvoiceChartPayload {
  teeth?: number[];
  overlays?: { crown?: number[]; rct?: number[] };
  states?: Record<number, string>;
}

interface PatientInfo {
  name?: string;
  mobile?: string;
  age?: number | string;
  gender?: string;
  patient_id?: string;
}

interface VisitInfo {
  visit_id?: string | number;
  visit_date?: string;
  visit_type?: string;
  [key: string]: unknown;
}

const formatDate = (value?: string) => {
  if (!value) return "-";
  const normalized = value.includes(" ") && !value.includes("T")
    ? value.replace(" ", "T")
    : value;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatMoney = (value?: number) => {
  const num = Number(value ?? 0);
  if (!Number.isFinite(num)) return "₹0.00";
  return `₹${num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const safeText = (value?: string | number | null) => {
  if (value === undefined || value === null || String(value).trim() === "") return "-";
  return String(value);
};

export default function Print() {
  const [params] = useSearchParams();
  const type = params.get("type");
  const visitId = params.get("visit");
  const billId = params.get("bill");
  const invoiceId = params.get("invoice");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [visit, setVisit] = useState<VisitInfo | null>(null);
  const [patient, setPatient] = useState<PatientInfo | null>(null);
  const [records, setRecords] = useState<RecordRow[]>([]);
  const [prescriptions, setPrescriptions] = useState<PrescriptionRow[]>([]);
  const [observationNotes, setObservationNotes] = useState<string>("");
  const [treatmentSummary, setTreatmentSummary] = useState<Record<string, unknown> | null>(null);
  const [chartSummary, setChartSummary] = useState<ChartSummaryShape | null>(null);
  const [clinicalBill, setClinicalBill] = useState<ClinicalBillData | null>(null);
  const [pharmacyBill, setPharmacyBill] = useState<PharmacyBillData | null>(null);
  const [referenceInvoice, setReferenceInvoice] = useState<ReferenceInvoiceData | null>(null);
  const [referenceInvoiceChart, setReferenceInvoiceChart] = useState<ReferenceInvoiceChartPayload | null>(null);
  const [showReferenceInvoiceChart, setShowReferenceInvoiceChart] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setVisit(null);
    setPatient(null);
    setRecords([]);
    setPrescriptions([]);
    setObservationNotes("");
    setTreatmentSummary(null);
    setChartSummary(null);
    setClinicalBill(null);
    setPharmacyBill(null);
    setReferenceInvoice(null);
    setReferenceInvoiceChart(null);
    setShowReferenceInvoiceChart(false);

    try {
      if (type === "doctor" && visitId) {
        const visitData = await apiGet(`/api/doctor/visit/${visitId}`) as Record<string, unknown>;
        setVisit(visitData as VisitInfo);
        setPatient((visitData.patient as PatientInfo) ?? null);
        const clinical = await apiGet(`/api/doctor/clinical-records/${visitId}?print=1`);
        setRecords(Array.isArray(clinical) ? (clinical as RecordRow[]) : []);
        const rx = await apiGet(`/api/doctor/prescriptions/visit/${visitId}`);
        setPrescriptions(Array.isArray(rx) ? (rx as PrescriptionRow[]) : []);
        const obs = await apiGet(`/api/doctor/observation-notes/${visitId}`) as { notes?: string };
        setObservationNotes(obs?.notes || "");
        const summary = await apiGet(`/api/doctor/treatment-summary/${visitId}`) as Record<string, unknown>;
        setTreatmentSummary(summary ?? null);
        const storedChart = localStorage.getItem(`chart-export-${visitId}`);
        if (storedChart) {
          try {
            setChartSummary(JSON.parse(storedChart) as ChartSummaryShape);
          } catch {
            // ignore
          }
        }
      }

      if (type === "bill" && billId) {
        const data = await apiGet(`/api/manager/print/clinical/${billId}`) as ClinicalBillData;
        if (data.error) throw new Error(data.error);
        setClinicalBill(data);
      }

      if (type === "pharmacy" && billId) {
        const data = await apiGet(`/api/pharmacy/print/bill/${billId}`) as PharmacyBillData;
        if (data.error) throw new Error(data.error);
        setPharmacyBill(data);
      }

      if (type === "reference-invoice" && invoiceId) {
        const data = await apiGet(`/api/manager/reference-invoice/${invoiceId}`) as ReferenceInvoiceData;
        setReferenceInvoice(data ?? null);

        let chartPayload: ReferenceInvoiceChartPayload | null = null;
        const tryLoad = (key: string) => {
          try {
            const raw = localStorage.getItem(key);
            if (!raw) return null;
            const parsed = JSON.parse(raw) as ReferenceInvoiceChartPayload;
            return parsed && typeof parsed === "object" ? parsed : null;
          } catch {
            return null;
          }
        };

        chartPayload = tryLoad(`reference-invoice:chart:${invoiceId}`);
        if (!chartPayload && data?.invoice_no && data.invoice_no !== invoiceId) {
          chartPayload = tryLoad(`reference-invoice:chart:${data.invoice_no}`);
        }
        setReferenceInvoiceChart(chartPayload);
        setShowReferenceInvoiceChart(Boolean(chartPayload));
      }
    } catch (e) {
      console.error("Print load error:", e);
      setError("Failed to load print data.");
    } finally {
      setLoading(false);
    }
  }, [type, visitId, billId, invoiceId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) return <div className="print-loading">Generating document...</div>;
  if (error) return <div className="print-error">{error}</div>;

  if (type === "doctor" && visit && patient) {
    return (
      <div className="print-page A4">
        <div className="print-watermark">Thaila Dental</div>
        <ClinicPrintHeader
          title="Clinical Report"
          subtitle="Prescription and Treatment Summary"
        />
        <div className="print-meta-grid">
          <div className="meta-row">
            <span>Patient Name</span>
            <strong>{safeText(patient.name)}</strong>
          </div>
          <div className="meta-row">
            <span>Visit Date</span>
            <strong>{formatDate(visit.visit_date)}</strong>
          </div>
          <div className="meta-row">
            <span>Age / Gender</span>
            <strong>{safeText(patient.age)} / {safeText(patient.gender)}</strong>
          </div>
          <div className="meta-row">
            <span>Visit ID</span>
            <strong>{safeText(visit.visit_id)}</strong>
          </div>
        </div>

        {chartSummary?.chart && Object.keys(chartSummary.chart).length > 0 && (
          <div className="section">
            <div className="section-title">Tooth Status Summary</div>
            <div className="chart-text-summary">
              {Object.entries(chartSummary.chart).map(([key, data]) => {
                const d = data as { status?: string; crown?: string; surfaces?: Record<string, unknown> };
                if (d.status === "none" && !d.crown) return null;
                return (
                  <span key={key} className="chart-tag">
                    <b>{key.toUpperCase()}:</b> {String(d.status ?? "")}
                    {d.crown ? ` (${String(d.crown)} Crown)` : ""}
                    {d.surfaces ? ` [${Object.keys(d.surfaces).join("")}]` : ""}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {treatmentSummary && (
          <div className="section">
            <div className="section-title">Treatment Summary</div>
            <div className="chart-text-summary">
              <span className="chart-tag">
                <b>Procedures:</b>{" "}
                {Array.isArray(treatmentSummary.procedures) && (treatmentSummary.procedures as string[]).length > 0
                  ? (treatmentSummary.procedures as string[]).join(", ")
                  : "-"}
              </span>
              <span className="chart-tag">
                <b>Teeth:</b>{" "}
                {Array.isArray(treatmentSummary.teeth) && (treatmentSummary.teeth as string[]).length > 0
                  ? (treatmentSummary.teeth as string[]).join(", ")
                  : "-"}
              </span>
            </div>
          </div>
        )}

        {observationNotes && (
          <div className="section">
            <div className="section-title">Observation Notes</div>
            <div className="chart-text-summary">
              <span className="chart-tag">{observationNotes}</span>
            </div>
          </div>
        )}

        {records.length > 0 && (
          <div className="section">
            <div className="section-title">Clinical Findings and Treatment</div>
            <table className="print-table">
              <thead>
                <tr>
                  <th className="print-w-12">Tooth</th>
                  <th className="print-w-28">Diagnosis</th>
                  <th className="print-w-30">Procedure</th>
                  <th className="print-w-30">Notes</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r, idx) => (
                  <tr key={idx}>
                    <td className="center-text"><strong>{r.tooth_number || "-"}</strong></td>
                    <td>{String(r.diagnosis ?? "")}</td>
                    <td>{String(r.procedure ?? "")}</td>
                    <td>{String(r.notes ?? "")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {prescriptions.length > 0 && (
          <div className="section">
            <div className="section-title">Prescription</div>
            <table className="print-table">
              <thead>
                <tr>
                  <th className="print-w-40">Medicine</th>
                  <th className="print-w-20">Dosage</th>
                  <th className="print-w-20">Duration</th>
                  <th className="print-w-20">Instructions</th>
                </tr>
              </thead>
              <tbody>
                {prescriptions.map((p, idx) => (
                  <tr key={idx}>
                    <td>
                      <strong>{p.drug_name}</strong> <small>{p.strength}</small>
                    </td>
                    <td className="center-text">{p.times_per_day}</td>
                    <td className="center-text">{p.days} Days</td>
                    <td>{p.instructions}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <ClinicPrintFooter
          note="Wishing you a smooth and healthy recovery."
          showSignature
          signatureLabel="Doctor's Signature"
        />
        <button type="button" className="no-print floating-print-btn" onClick={() => window.print()}>
          Print
        </button>
      </div>
    );
  }

  if (type === "bill" && clinicalBill?.bill && clinicalBill?.patient) {
    const b = clinicalBill.bill;
    const p = clinicalBill.patient;
    const payments = Array.isArray(clinicalBill.payments) ? clinicalBill.payments : [];
    const lastPayment = payments.length > 0 ? payments[payments.length - 1] : null;
    const items = (clinicalBill.items || []).map((item) => ({
      desc: item.procedure_name,
      teeth: item.teeth,
      qty: item.quantity ?? 0,
      unit: item.unit_price ?? 0,
      amount: item.total_price ?? (item.quantity ?? 0) * (item.unit_price ?? 0),
    }));
    const net = Number(b.net ?? 0);
    const balance = Number(b.balance ?? 0);
    const paid = Math.max(net - balance, 0);
    const previousPaid = lastPayment ? Math.max(paid - Number(lastPayment.amount || 0), 0) : paid;

    return (
      <div className="print-page A5">
        <div className="print-watermark">Thaila Dental</div>
        <ClinicPrintHeader
          title="Clinical Billing Receipt"
          subtitle="Professional dental services"
          meta={<span className="status-pill">{safeText(b.status || "Pending")}</span>}
        />
        <div className="print-meta-grid">
          <div className="meta-row">
            <span>Bill Number</span>
            <strong>{safeText(b.id)}</strong>
          </div>
          <div className="meta-row">
            <span>Date</span>
            <strong>{formatDate(b.created_at)}</strong>
          </div>
          <div className="meta-row">
            <span>Patient</span>
            <strong>{safeText(p.name)}</strong>
          </div>
          <div className="meta-row">
            <span>Patient ID</span>
            <strong>{safeText(p.patient_id)}</strong>
          </div>
        </div>

        <table className="print-table mt-20">
          <thead>
            <tr>
              <th>Procedure</th>
              <th className="print-w-18">Tooth</th>
              <th className="right-text">Qty</th>
              <th className="right-text">Unit</th>
              <th className="right-text">Amount</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <tr key={i}>
                <td>{item.desc}</td>
                <td className="center-text">{item.teeth ? String(item.teeth) : "-"}</td>
                <td className="right-text">{item.qty}</td>
                <td className="right-text">{formatMoney(item.unit)}</td>
                <td className="right-text">{formatMoney(item.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="financial-summary">
          <div className="row"><span>Total Amount (Billed)</span> <span>{formatMoney(b.gross)}</span></div>
          {(b.discount ?? 0) > 0 && (
            <div className="row discount"><span>Discount</span> <span>- {formatMoney(b.discount)}</span></div>
          )}
          <div className="row total"><span>Net Total</span> <span>{formatMoney(b.net)}</span></div>

          {payments.length > 0 ? (
            <div className="payment-breakdown">
              <div className="payment-title">Payment Breakdown</div>
              <div className="payment-lines">
                <div className="payment-line">
                  <span>Previous Payments</span>
                  <span className="paid-text">{formatMoney(previousPaid)}</span>
                </div>
                {lastPayment ? (
                  <div className="payment-line current">
                    <span>Current Payment ({safeText(lastPayment.method)} • {formatDate(lastPayment.date)})</span>
                    <span className="paid-text">{formatMoney(lastPayment.amount)}</span>
                  </div>
                ) : null}
                <div className="payment-line balance">
                  <span>Remaining Balance</span>
                  <span className="balance-text">{formatMoney(b.balance)}</span>
                </div>
              </div>

              <div className="payment-history">
                <div className="payment-title small">Payment History</div>
                <table className="print-table compact">
                  <thead>
                    <tr>
                      <th className="print-w-34">Date</th>
                      <th>Mode</th>
                      <th className="right-text">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((pay, idx) => {
                      const isCurrent = lastPayment?.id != null && pay.id === lastPayment.id
                        ? true
                        : idx === payments.length - 1;
                      return (
                        <tr key={String(pay.id ?? idx)} className={isCurrent ? "current-payment-row" : ""}>
                          <td className="center-text">{formatDate(pay.date)}</td>
                          <td>{safeText(pay.method)}</td>
                          <td className="right-text">{formatMoney(pay.amount)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <>
              <div className="row paid"><span>Amount Paid</span> <span>{formatMoney(paid)}</span></div>
              {(b.balance ?? 0) > 0 && (
                <div className="row balance"><span>Balance Due</span> <span>{formatMoney(b.balance)}</span></div>
              )}
            </>
          )}
        </div>

        <ClinicPrintFooter note="Thank you for visiting Thaila Dental & Oral Care." />
        <button type="button" className="no-print floating-print-btn" onClick={() => window.print()}>
          Print
        </button>
      </div>
    );
  }

  if (type === "pharmacy" && pharmacyBill?.bill) {
    const b = pharmacyBill.bill;
    const p = pharmacyBill.patient;
    const items = pharmacyBill.items || [];

    return (
      <div className="print-page A5">
        <div className="print-watermark">Thaila Dental</div>
        <ClinicPrintHeader
          title="Pharmacy Bill"
          subtitle="Medication receipt"
          meta={<span className="status-pill">{safeText(b.bill_type)}</span>}
        />
        <div className="print-meta-grid">
          <div className="meta-row">
            <span>Bill Number</span>
            <strong>{safeText(b.id)}</strong>
          </div>
          <div className="meta-row">
            <span>Date</span>
            <strong>{formatDate(b.created_at)}</strong>
          </div>
          <div className="meta-row">
            <span>Patient</span>
            <strong>{safeText(p?.name)}</strong>
          </div>
          <div className="meta-row">
            <span>Mobile</span>
            <strong>{safeText(p?.mobile)}</strong>
          </div>
        </div>

        <table className="print-table mt-20">
          <thead>
            <tr>
              <th>Item</th>
              <th className="right-text">Qty</th>
              <th className="right-text">Unit</th>
              <th className="right-text">Amount</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <tr key={i}>
                <td>{item.name}</td>
                <td className="right-text">{item.qty}</td>
                <td className="right-text">{formatMoney(item.unit_price)}</td>
                <td className="right-text">{formatMoney(item.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="financial-summary">
          <div className="row total"><span>Total</span> <span>{formatMoney(b.total)}</span></div>
        </div>

        <ClinicPrintFooter note="Please keep this receipt for reference." />
        <button type="button" className="no-print floating-print-btn" onClick={() => window.print()}>
          Print
        </button>
      </div>
    );
  }

  if (type === "reference-invoice" && referenceInvoice?.invoice_no) {
    const inv = referenceInvoice;
    const items = Array.isArray(inv.items) ? inv.items : [];
    const chartData = referenceInvoiceChart;
    const computedTotal = items.reduce(
      (sum, item) => sum + Number(item.total_price ?? (item.quantity ?? 0) * (item.unit_price ?? 0)),
      0
    );
    const totalAmount = Number(inv.total_amount ?? computedTotal ?? 0);
    const discount = Number(inv.discount ?? 0);
    const finalAmount = Number(inv.final_amount ?? Math.max(totalAmount - discount, 0));

    return (
      <div className="print-page A4">
        <div className="print-watermark">Thaila Dental</div>
        <ClinicPrintHeader
          title="Reference Invoice (Estimate)"
          subtitle="For patient reference only"
          meta={<span className="status-pill">{safeText(inv.status || "DRAFT")}</span>}
        />

        <div className="print-meta-grid">
          <div className="meta-row">
            <span>Invoice No</span>
            <strong>{safeText(inv.invoice_no)}</strong>
          </div>
          <div className="meta-row">
            <span>Date</span>
            <strong>{formatDate(inv.date)}</strong>
          </div>
          <div className="meta-row">
            <span>Patient</span>
            <strong>{safeText(inv.patient_name)}</strong>
          </div>
          <div className="meta-row">
            <span>Doctor</span>
            <strong>{safeText(inv.doctor_name)}</strong>
          </div>
        </div>

        <div className="no-print refin-actions">
          <label className="refin-toggle">
            <input
              type="checkbox"
              checked={showReferenceInvoiceChart}
              onChange={(e) => setShowReferenceInvoiceChart(e.target.checked)}
              disabled={!chartData}
            />
            Include tooth chart
          </label>
          {!chartData ? <span className="refin-muted">No chart data saved for this invoice.</span> : null}
        </div>

        {showReferenceInvoiceChart && chartData ? (
          <div className="refinv-chart">
            <ReadonlyToothChart
              teeth={Array.isArray(chartData.teeth) ? chartData.teeth : []}
              overlays={chartData.overlays}
              states={chartData.states}
            />
          </div>
        ) : null}

        {inv.notes ? (
          <div className="refinv-notes">
            <div className="section-title">Notes</div>
            <div className="refinv-notes-body">{safeText(inv.notes)}</div>
          </div>
        ) : null}

        <table className="print-table mt-20">
          <thead>
            <tr>
              <th>Description</th>
              <th className="right-text print-w-12">Qty</th>
              <th className="right-text print-w-18">Unit</th>
              <th className="right-text print-w-18">Amount</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={4} className="center-text muted">No items</td>
              </tr>
            ) : (
              items.map((item, idx) => {
                const qty = Number(item.quantity ?? 0);
                const unit = Number(item.unit_price ?? 0);
                const amount = Number(item.total_price ?? qty * unit);
                return (
                  <tr key={String(item.id ?? idx)}>
                    <td>{safeText(item.description)}</td>
                    <td className="right-text">{qty}</td>
                    <td className="right-text">{formatMoney(unit)}</td>
                    <td className="right-text">{formatMoney(amount)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        <div className="financial-summary">
          <div className="row"><span>Total Amount</span> <span>{formatMoney(totalAmount)}</span></div>
          {discount > 0 ? (
            <div className="row discount"><span>Discount</span> <span>- {formatMoney(discount)}</span></div>
          ) : null}
          <div className="row total"><span>Final Amount</span> <span>{formatMoney(finalAmount)}</span></div>
        </div>

        <ClinicPrintFooter note="This is a reference estimate only. Final cost may vary depending on clinical findings." />
        <button type="button" className="no-print floating-print-btn" onClick={() => window.print()}>
          Print
        </button>
      </div>
    );
  }

  return <div className="print-error">No data found for the requested document.</div>;
}
